import React, { useState, useEffect } from 'react';
import { FaStethoscope, FaUserFriends, FaDollarSign, FaBuilding, FaCalendarAlt, FaUsers, FaExclamationCircle, FaFileAlt, FaPhone, FaCheckCircle } from 'react-icons/fa';

const ComplaintForm = ({ category, onSubmit }) => {
  const [form, setForm] = useState({
    complaintCategory: category,
    dateOfEvent: new Date().toISOString().split('T')[0],
    location: '',
    staffInvolved: '',
    patientExperienceArea: '',
    description: '',
    desiredResolution: '',
    severity: 'Low',
    contactedBy: '',
    followUpNeeded: 'No'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit(form);
      setIsSubmitting(false);
    }, 100);
  };

  // Get icon based on category
  const getCategoryIcon = () => {
    if (category.includes('Clinical Care')) return FaStethoscope;
    if (category.includes('Customer Service')) return FaUserFriends;
    if (category.includes('Billing') || category.includes('Financial')) return FaDollarSign;
    if (category.includes('Facility') || category.includes('Environment')) return FaBuilding;
    return FaExclamationCircle; // Default icon
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-indigo-100">
            <CategoryIcon className="size-8 text-indigo-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {category}
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report patient complaint details and resolution requirements
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={submit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and where the complaint occurred
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateOfEvent" className="block text-sm/6 font-medium text-gray-900">
                      Date of Event <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateOfEvent"
                        name="dateOfEvent"
                        type="date"
                        value={form.dateOfEvent}
                        onChange={update}
                        required
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm/6 font-medium text-gray-900">
                      Location/Operatory
                    </label>
                    <div className="mt-2">
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={form.location}
                        onChange={update}
                        placeholder="Enter location or operatory"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Staff and Context Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUsers className="size-5 text-gray-400" />
                  Staff and Context
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Staff members involved and area of concern
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="staffInvolved" className="block text-sm/6 font-medium text-gray-900">
                      Staff Involved
                    </label>
                    <div className="mt-2">
                      <input
                        id="staffInvolved"
                        name="staffInvolved"
                        type="text"
                        value={form.staffInvolved}
                        onChange={update}
                        placeholder="Enter staff members involved"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="patientExperienceArea" className="block text-sm/6 font-medium text-gray-900">
                      Area of Concern
                    </label>
                    <div className="mt-2">
                      <select
                        id="patientExperienceArea"
                        name="patientExperienceArea"
                        value={form.patientExperienceArea}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option value="">Select area of concern</option>
                        <option>Wait Time</option>
                        <option>Communication</option>
                        <option>Billing/Financial</option>
                        <option>Clinical Care</option>
                        <option>Facility/Environment</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="severity" className="block text-sm/6 font-medium text-gray-900">
                      Severity
                    </label>
                    <div className="mt-2">
                      <select
                        id="severity"
                        name="severity"
                        value={form.severity}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>Low</option>
                        <option>Moderate</option>
                        <option>High</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complaint Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Complaint Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Detailed description of the complaint and desired resolution
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="description" className="block text-sm/6 font-medium text-gray-900">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="description"
                        name="description"
                        rows={6}
                        value={form.description}
                        onChange={update}
                        required
                        placeholder="Provide a detailed description of the complaint..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="desiredResolution" className="block text-sm/6 font-medium text-gray-900">
                      Desired Resolution
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="desiredResolution"
                        name="desiredResolution"
                        rows={4}
                        value={form.desiredResolution}
                        onChange={update}
                        placeholder="Describe what resolution or outcome the patient is seeking..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Follow-up Information Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPhone className="size-5 text-gray-400" />
                  Follow-up Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Contact and follow-up details
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contactedBy" className="block text-sm/6 font-medium text-gray-900">
                      Contacted By
                    </label>
                    <div className="mt-2">
                      <input
                        id="contactedBy"
                        name="contactedBy"
                        type="text"
                        value={form.contactedBy}
                        onChange={update}
                        placeholder="Enter name of person who contacted patient"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="followUpNeeded" className="block text-sm/6 font-medium text-gray-900">
                      Follow-up Needed
                    </label>
                    <div className="mt-2">
                      <select
                        id="followUpNeeded"
                        name="followUpNeeded"
                        value={form.followUpNeeded}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComplaintForm;
